// src/pages/ClientManagement.jsx
import React, { useState, useEffect } from 'react';
import { 
    Plus, 
    Search, 
    Edit, 
    Trash2, 
    Eye, 
    Building2, 
    User, 
    Mail, 
    Phone,
    MapPin,
    TrendingUp,
    Users,
    DollarSign , LayoutDashboard 
} from 'lucide-react';
import NewClientModal from './NewClientModal';
import apiService from '../services/api';
import { useNavigate } from 'react-router-dom';


const ClientManagement = () => {
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
    const [stats, setStats] = useState({
        totalClients: 0,
        activeClients: 0,
        prospects: 0,
        totalRevenue: 0
    });

    // Modal states
    const [showNewClientModal, setShowNewClientModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const navigate = useNavigate();
    

    useEffect(() => {
        loadClients();
        loadStats();
    }, []);

    useEffect(() => {
        filterClients();
    }, [clients, searchTerm, selectedStatus, selectedIndustry]);

    const loadClients = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.getAllClients({
                page: pagination.page,
                limit: pagination.limit
            });
            
            if (response.success) {
                setClients(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination.total
                }));
            }
        } catch (error) {
            console.error('Failed to load clients:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await apiService.getClientStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    };

    const filterClients = () => {
        let filtered = clients.filter(client => {
            const matchesSearch = 
                client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (client.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                client.clientEmail.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesStatus = !selectedStatus || client.status === selectedStatus;
            const matchesIndustry = !selectedIndustry || client.industry === selectedIndustry;
            
            return matchesSearch && matchesStatus && matchesIndustry;
        });

        setFilteredClients(filtered);
    };

    const handleCreateClient = async (clientData) => {
        try {
            const response = await apiService.createClient(clientData);
            if (response.success) {
                setClients(prev => [response.data, ...prev]);
                setShowNewClientModal(false);
                alert('Client created successfully!');
            } else {
                alert('Failed to create client: ' + response.message);
            }
        } catch (error) {
            console.error('Error creating client:', error);
            alert('Failed to create client. Please try again.');
        }
    };

    const handleDeleteClient = async (client) => {
        if (window.confirm(`Are you sure you want to delete ${client.clientName}?`)) {
            try {
                const response = await apiService.deleteClient(client._id);
                if (response.success) {
                    setClients(prev => prev.filter(c => c._id !== client._id));
                    alert('Client deleted successfully!');
                } else {
                    alert('Failed to delete client: ' + response.message);
                }
            } catch (error) {
                console.error('Error deleting client:', error);
                alert('Failed to delete client. Please try again.');
            }
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { bg: '#ecfdf5', color: '#059669', text: 'Active' },
            inactive: { bg: '#f3f4f6', color: '#6b7280', text: 'Inactive' },
            prospect: { bg: '#fffbeb', color: '#f59e0b', text: 'Prospect' }
        };

        const config = statusConfig[status] || statusConfig.active;

        return (
            <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: config.bg,
                color: config.color
            }}>
                {config.text}
            </span>
        );
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f8fafc',
            padding: '24px'
        }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '32px' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '24px',
                        flexWrap: 'wrap',
                        gap: '16px'
                    }}>
                        <div>
                            <h1 style={{
                                fontSize: '32px',
                                fontWeight: '700',
                                color: '#1f2937',
                                margin: '0 0 8px 0'
                            }}>
                                Client Management
                            </h1>
                            <p style={{
                                fontSize: '16px',
                                color: '#6b7280',
                                margin: 0
                            }}>
                                Manage your client database and relationships
                            </p>
                        </div>

                        <button
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 20px',
                                backgroundColor: 'white',
                                color: '#374151',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={() => navigate('/dashboard')}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f9fafb';
                                e.target.style.borderColor = '#9ca3af';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.borderColor = '#d1d5db';
                            }}
                        >
                            <LayoutDashboard  size={18} />
                            Dashboard
                        </button>

                        <button
                            onClick={() => setShowNewClientModal(true)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 20px',
                                backgroundColor: '#1e40af',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            <Plus size={18} />
                            New Client
                        </button>
                    </div>

                    {/* Stats Cards */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '20px'
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '12px'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: '#eff6ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Users size={24} color="#1e40af" />
                                </div>
                                <div>
                                    <p style={{
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#1f2937',
                                        margin: 0
                                    }}>
                                        {stats.totalClients}
                                    </p>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        margin: 0
                                    }}>
                                        Total Clients
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: 'white',
                            padding: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '12px'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: '#ecfdf5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <TrendingUp size={24} color="#059669" />
                                </div>
                                <div>
                                    <p style={{
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#1f2937',
                                        margin: 0
                                    }}>
                                        {stats.activeClients}
                                    </p>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        margin: 0
                                    }}>
                                        Active Clients
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: 'white',
                            padding: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '12px'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: '#fffbeb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <User size={24} color="#f59e0b" />
                                </div>
                                <div>
                                    <p style={{
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#1f2937',
                                        margin: 0
                                    }}>
                                        {stats.prospects}
                                    </p>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        margin: 0
                                    }}>
                                        Prospects
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: 'white',
                            padding: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '12px'
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: '#ecfdf5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <DollarSign size={24} color="#059669" />
                                </div>
                                <div>
                                    <p style={{
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#1f2937',
                                        margin: 0
                                    }}>
                                        {formatCurrency(stats.totalRevenue)}
                                    </p>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#6b7280',
                                        margin: 0
                                    }}>
                                        Total Revenue
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                    marginBottom: '24px'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        {/* Search */}
                        <div style={{
                            position: 'relative',
                            flex: '1',
                            minWidth: '300px'
                        }}>
                            <Search
                                size={20}
                                style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af'
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Search clients..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 12px 12px 44px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                            />
                        </div>

                        {/* Status Filter */}
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            style={{
                                padding: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                outline: 'none'
                            }}
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="prospect">Prospect</option>
                        </select>

                        {/* Industry Filter */}
                        <select
                            value={selectedIndustry}
                            onChange={(e) => setSelectedIndustry(e.target.value)}
                            style={{
                                padding: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                outline: 'none'
                            }}
                        >
                            <option value="">All Industries</option>
                            <option value="Construction">Construction</option>
                            <option value="Real Estate">Real Estate</option>
                            <option value="Manufacturing">Manufacturing</option>
                            <option value="Healthcare">Healthcare</option>
                            <option value="Education">Education</option>
                            <option value="Government">Government</option>
                            <option value="Hospitality">Hospitality</option>
                            <option value="Retail">Retail</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                </div>

                {/* Clients Table */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden'
                }}>
                    {isLoading ? (
                        <div style={{
                            padding: '48px',
                            textAlign: 'center',
                            color: '#6b7280'
                        }}>
                            Loading clients...
                        </div>
                    ) : filteredClients.length === 0 ? (
                        <div style={{
                            padding: '48px',
                            textAlign: 'center',
                            color: '#6b7280'
                        }}>
                            No clients found
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                <tr style={{ backgroundColor: '#f9fafb' }}>
                                    <th style={{
                                        padding: '16px',
                                        textAlign: 'left',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        Client
                                    </th>
                                    <th style={{
                                        padding: '16px',
                                        textAlign: 'left',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        Contact
                                    </th>
                                    <th style={{
                                        padding: '16px',
                                        textAlign: 'left',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        Industry
                                    </th>
                                    <th style={{
                                        padding: '16px',
                                        textAlign: 'left',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        Status
                                    </th>
                                    <th style={{
                                        padding: '16px',
                                        textAlign: 'right',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        Revenue
                                    </th>
                                    <th style={{
                                        padding: '16px',
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        textTransform: 'uppercase',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredClients.map((client, index) => (
                                    <tr
                                        key={client._id}
                                        style={{
                                            borderBottom: index < filteredClients.length - 1 ? '1px solid #f3f4f6' : 'none'
                                        }}
                                    >
                                        <td style={{ padding: '16px' }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#eff6ff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {client.companyName ? (
                                                        <Building2 size={20} color="#1e40af" />
                                                    ) : (
                                                        <User size={20} color="#1e40af" />
                                                    )}
                                                </div>
                                                <div>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        color: '#1f2937',
                                                        marginBottom: '2px'
                                                    }}>
                                                        {client.clientName}
                                                    </div>
                                                    {client.companyName && (
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#6b7280'
                                                        }}>
                                                            {client.companyName}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px' }}>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    fontSize: '12px',
                                                    color: '#6b7280'
                                                }}>
                                                    <Mail size={12} />
                                                    <span>{client.clientEmail}</span>
                                                </div>
                                                {client.clientPhone && (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        fontSize: '12px',
                                                        color: '#6b7280'
                                                    }}>
                                                        <Phone size={12} />
                                                        <span>{client.clientPhone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px' }}>
                                            <span style={{
                                                fontSize: '14px',
                                                color: '#1f2937'
                                            }}>
                                                {client.industry || 'Not specified'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px' }}>
                                            {getStatusBadge(client.status)}
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'right' }}>
                                            <span style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#1f2937'
                                            }}>
                                                {formatCurrency(client.totalRevenue || 0)}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px' }}>
                                            <div style={{
                                                display: 'flex',
                                                gap: '8px',
                                                justifyContent: 'center'
                                            }}>
                                                <button
                                                    onClick={() => {
                                                        setSelectedClient(client);
                                                        setShowViewModal(true);
                                                    }}
                                                    style={{
                                                        padding: '6px',
                                                        backgroundColor: '#eff6ff',
                                                        color: '#1e40af',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="View"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedClient(client);
                                                        setShowNewClientModal(true);
                                                    }}
                                                    style={{
                                                        padding: '6px',
                                                        backgroundColor: '#f0fdf4',
                                                        color: '#059669',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="Edit"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClient(client)}
                                                    style={{
                                                        padding: '6px',
                                                        backgroundColor: '#fef2f2',
                                                        color: '#dc2626',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* New Client Modal */}
            {showNewClientModal && (
                <NewClientModal
                    onClose={() => {
                        setShowNewClientModal(false);
                        setSelectedClient(null);
                    }}
                    onSave={handleCreateClient}
                    initialClient={selectedClient}
                    isQuickCreate={false}
                />
            )}
        </div>
    );
};

export default ClientManagement;